// src/pages/misc/landing/PipelinePreview.tsx
// Lab-only pipeline card. Same outer size as the other hero cards. Not used on /landing.
// [VERIFY] The line above is out of date: this card IS used on the live landing (inside LandingHeroPair in HeroPreviews.tsx, with
//   maxPerColumn={4}, positioned behind the workspace card). Fake sample leads on purpose. Needs components/leads/LeadInitialsBadge.tsx.
// [SPACING] Each column is 168px wide (CHIP_W) with 10px gaps; extra columns are clipped by the card's right edge on purpose.
import React from 'react';
import { MessageCircle, Phone, Video } from 'lucide-react';
import LeadInitialsBadge from '../../../components/leads/LeadInitialsBadge';

const STAGES = [
  { name: 'Discovery', color: '#3b82f6' },
  { name: 'Qualified', color: '#8b5cf6' },
  { name: 'Presentation', color: '#6366f1' },
  { name: 'Negotiation', color: '#f59e0b' },
] as const;

/** A little under half a Leads-list row. Extra columns clip on the right. */
const CHIP_W = 168;

const COLUMNS: {
  stage: (typeof STAGES)[number]['name'];
  leads: { name: string; when: string; calls: number; messages: number; zooms: number }[];
}[] = [
  {
    stage: 'Discovery',
    leads: [
      { name: 'Ana Gutierrez', when: 'Oct 6 · 8:12', calls: 1, messages: 3, zooms: 0 },
      { name: 'Camila Ortega', when: 'Oct 5 · 4:40', calls: 2, messages: 2, zooms: 0 },
    ],
  },
  {
    stage: 'Qualified',
    leads: [
      { name: 'Carlos Mendez', when: 'Oct 4 · 11:05', calls: 4, messages: 7, zooms: 1 },
      { name: 'Mateo Flores', when: 'Oct 3 · 2:18', calls: 3, messages: 5, zooms: 0 },
      { name: 'Sofia Vargas', when: 'Oct 2 · 9:41', calls: 6, messages: 9, zooms: 1 },
      { name: 'Elena Torres', when: 'Oct 1 · 6:07', calls: 2, messages: 4, zooms: 1 },
      { name: 'Roberto Silva', when: 'Sep 30 · 8:02', calls: 5, messages: 6, zooms: 0 },
    ],
  },
  {
    stage: 'Presentation',
    leads: [
      { name: 'Luis Hernandez', when: 'Sep 30 · 3:22', calls: 8, messages: 12, zooms: 2 },
      { name: 'Isabel Cruz', when: 'Sep 29 · 10:15', calls: 5, messages: 8, zooms: 1 },
      { name: 'Patricia Morales', when: 'Sep 28 · 1:48', calls: 7, messages: 10, zooms: 2 },
    ],
  },
  {
    stage: 'Negotiation',
    leads: [{ name: 'Diego Ramirez', when: 'Sep 27 · 5:33', calls: 9, messages: 14, zooms: 3 }],
  },
];

const ACTIONS = [
  { key: 'calls', Icon: Phone },
  { key: 'messages', Icon: MessageCircle },
  { key: 'zooms', Icon: Video },
] as const;

/** Hero passes 4 so each column stops at a full card instead of a chopped one. */
const PipelinePreview: React.FC<{ maxPerColumn?: number }> = ({ maxPerColumn }) => (
  <div className="px-5 py-4 h-full flex flex-col gap-2 overflow-hidden">
    <p className="text-sm uppercase tracking-[0.2em] font-semibold px-1" style={{ color: '#4b5563' }}>Pipeline</p>
    <div className="flex items-start gap-2.5 min-h-0">
      {COLUMNS.map((column) => {
        const color = STAGES.find((stage) => stage.name === column.stage)?.color ?? '#3b82f6';
        return (
          <div key={column.stage} className="flex flex-col gap-1.5 shrink-0" style={{ width: CHIP_W }}>
            {column.leads.map((lead) => (
              <div
                key={lead.name}
                className="px-2.5 py-2 flex flex-col gap-1.5 min-w-0"
                style={{
                  background: 'rgba(255,255,255,0.94)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
                  borderRadius: '1rem',
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <LeadInitialsBadge
                    name={lead.name}
                    stage={column.stage === 'Presentation' ? 'Won' : column.stage}
                    stageColor={color}
                    size={26}
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-[13px] truncate leading-tight" style={{ color: '#111827' }}>
                      {lead.name}
                    </p>
                    <p className="text-[11px] truncate leading-tight mt-0.5" style={{ color: '#1d4ed8' }}>
                      {lead.when}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 pl-0.5">
                  {ACTIONS.map(({ key, Icon }) => (
                    <span key={key} className="inline-flex items-center gap-1 min-w-0">
                      <Icon size={12} color="#6b7280" strokeWidth={2.25} />
                      <span className="text-[11px] font-semibold tabular-nums" style={{ color: '#374151' }}>
                        {lead[key]}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  </div>
);

export default PipelinePreview;
